import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../App';
import {
  ArrowLeft, ArrowRight, TrendingUp, Target, Leaf, AlertTriangle, Lock
} from 'lucide-react';
import { PartnerLogos } from '../components/PartnerLogos';

const BRAND_LOGO_DARK = "/benchmark-ph-dark.png";
const BRAND_LOGO_LIGHT = "/benchmark-ph-light.png";

export default function StateOfEsgPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api.get('/api/public/state-of-esg')
      .then(res => setData(res.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Hero */}
      <section className="hero-gradient px-6 md:px-12 lg:px-24 pt-8 pb-16">
        <div className="flex items-center justify-between mb-12">
          <img src={BRAND_LOGO_DARK} alt="Benchmark PH" className="h-10 md:h-12 object-contain" />
          <Link to="/" className="inline-flex items-center gap-2 text-slate-300 hover:text-white text-sm transition-colors" data-testid="back-to-home">
            <ArrowLeft className="w-4 h-4" />
            Home
          </Link>
        </div>
        <div className="max-w-4xl">
          <div className="inline-flex items-center gap-2 bg-accent/20 text-accent px-4 py-2 rounded-full text-sm font-medium mb-6">
            <TrendingUp className="w-4 h-4" />
            Anonymized Industry Insights
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight mb-4">
            State of <span className="text-accent">IT-BPM ESG</span>
          </h1>
          <p className="text-slate-300 text-lg max-w-2xl">
            A live pulse of Environmental, Social and Governance performance across the
            Philippine IT-BPM industry — powered by the IBPAP ESG Double Materiality Survey.
          </p>
        </div>

        {/* Participation stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mt-12 max-w-3xl" data-testid="public-participation-stats">
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="text-4xl font-bold text-accent mb-1">{data?.participating_companies ?? 0}</div>
            <div className="text-slate-400 text-sm">Companies Invited</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="text-4xl font-bold text-white mb-1">{data?.completed_count ?? 0}</div>
            <div className="text-slate-400 text-sm">Responses Submitted</div>
          </div>
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/10">
            <div className="text-4xl font-bold text-accent mb-1">{data?.completion_rate ?? 0}%</div>
            <div className="text-slate-400 text-sm">Completion Rate</div>
          </div>
        </div>
      </section>

      {/* Body */}
      <main className="px-6 md:px-12 lg:px-24 py-12 max-w-6xl mx-auto">
        {!data?.sample_ready ? (
          <div className="bg-white rounded-2xl border border-slate-200 p-10 text-center" data-testid="teaser-placeholder">
            <Lock className="w-12 h-12 text-accent mx-auto mb-4" />
            <h2 className="text-2xl font-bold text-primary mb-2">Industry results are being compiled</h2>
            <p className="text-slate-500 max-w-xl mx-auto">
              To protect respondent anonymity, aggregated insights are published once at least{' '}
              <span className="font-semibold text-primary">{data?.min_sample ?? 3} companies</span> have completed
              the survey. {data?.completed_count ?? 0} response{(data?.completed_count ?? 0) === 1 ? ' has' : 's have'} been
              submitted so far — check back soon!
            </p>
          </div>
        ) : (
          <div className="space-y-8">
            {/* Top material issues */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="top-issues-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                  <Target className="w-5 h-5 text-accent-dark" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-primary">Top Material ESG Issues</h2>
                  <p className="text-sm text-slate-500">Highest combined financial + impact relevance (scale 1–5)</p>
                </div>
              </div>
              <div className="space-y-4">
                {data.top_issues.map((issue, i) => (
                  <div key={issue.name} className="flex items-center gap-4" data-testid={`top-issue-${i}`}>
                    <span className="w-8 h-8 rounded-full bg-primary text-accent text-sm font-bold flex items-center justify-center flex-shrink-0">{i + 1}</span>
                    <div className="flex-1">
                      <div className="flex justify-between text-sm mb-1">
                        <span className="font-medium text-slate-700">{issue.name}</span>
                        <span className="font-bold text-primary">{issue.score}</span>
                      </div>
                      <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-accent rounded-full" style={{ width: `${(issue.score / 5) * 100}%` }} />
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* KPI highlights */}
            <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="kpi-highlights-section">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-10 h-10 bg-accent/10 rounded-xl flex items-center justify-center">
                  <TrendingUp className="w-5 h-5 text-accent-dark" />
                </div>
                <div>
                  <h2 className="text-xl font-bold text-primary">Industry KPI Averages</h2>
                  <p className="text-sm text-slate-500">Anonymized averages across all completed responses</p>
                </div>
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                {data.kpi_highlights.map((kpi) => (
                  <div key={kpi.label} className="bg-slate-50 rounded-xl p-5">
                    <div className="text-3xl font-bold text-primary">{kpi.value}<span className="text-lg text-accent-dark ml-1">{kpi.unit}</span></div>
                    <div className="text-xs text-slate-500 mt-1">{kpi.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Targets + risks */}
            <div className="grid md:grid-cols-2 gap-8">
              <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="esg-targets-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-emerald-100 rounded-xl flex items-center justify-center">
                    <Leaf className="w-5 h-5 text-emerald-600" />
                  </div>
                  <h2 className="text-xl font-bold text-primary">ESG Targets Adoption</h2>
                </div>
                <div className="text-5xl font-bold text-primary mb-2">{data.esg_targets_pct}%</div>
                <p className="text-sm text-slate-500">of participating companies have formal ESG targets in place</p>
                <div className="h-3 bg-slate-100 rounded-full overflow-hidden mt-4">
                  <div className="h-full bg-emerald-500 rounded-full" style={{ width: `${data.esg_targets_pct}%` }} />
                </div>
              </div>

              <div className="bg-white rounded-2xl border border-slate-200 p-8" data-testid="top-risks-section">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-amber-100 rounded-xl flex items-center justify-center">
                    <AlertTriangle className="w-5 h-5 text-amber-600" />
                  </div>
                  <h2 className="text-xl font-bold text-primary">Most Cited ESG Risks</h2>
                </div>
                <div className="space-y-3">
                  {data.top_risks.map((r, i) => (
                    <div key={r.risk} className="flex items-center justify-between bg-slate-50 rounded-xl px-4 py-3">
                      <span className="text-sm font-medium text-slate-700">{i + 1}. {r.risk}</span>
                      <span className="text-xs font-semibold text-amber-700 bg-amber-100 px-2 py-1 rounded-full">{r.count} mention{r.count > 1 ? 's' : ''}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        )}

        {/* CTA */}
        <div className="mt-12 bg-brand-navy rounded-2xl p-10 text-center" data-testid="public-cta">
          <img src={BRAND_LOGO_DARK} alt="Benchmark PH" className="h-10 object-contain mx-auto mb-6" />
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-3">Is your company part of the benchmark?</h2>
          <p className="text-slate-300 max-w-xl mx-auto mb-6">
            Participation is by invitation. If your company received its Company Code and Invite Code,
            sign in to complete the survey — or contact the IBPAP ESG Program Team to join.
          </p>
          <Link to="/auth" className="btn-accent inline-flex items-center gap-2" data-testid="cta-signin-btn">
            Member Sign In
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </main>

      {/* Footer */}
      <footer className="px-4 py-6 text-center text-slate-500 text-sm border-t border-slate-200 bg-white">
        <img src={BRAND_LOGO_LIGHT} alt="Benchmark PH" className="h-8 object-contain mx-auto mb-3 opacity-80" />
        <PartnerLogos className="mb-3" />
        Developed for <span className="text-slate-600">IBPAP</span> by{' '}
        <a href="https://sustek.ai/solutions/soctech" target="_blank" rel="noopener noreferrer" className="text-emerald-600 hover:text-emerald-700 hover:underline">
          SusTek SocTech Labs
        </a>
      </footer>
    </div>
  );
}
